"use client";
import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef } from "react";
import { Heading2 } from "../typography";
import { ScrollItem } from "../nsw-water-compliance/scroll-item";
import { TheResults } from "./the-results";
import { LazyDotLottie } from "../shared/lazy-dotlottie";
import { cn } from "@/app/utils/className";
import Image from "next/image";

export const HowItWorks = () => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollItemsRef = useRef<(HTMLDivElement | null)[]>([]);

  const calculateScrollDistance = () => {
    const content = contentRef.current;
    const scrollItems = scrollItemsRef.current.filter(Boolean);

    if (!content || !scrollItems.length) return 0;

    const gap = parseFloat(getComputedStyle(content).gap) || 0;
    const totalWidth =
      scrollItems.length * (scrollItems[0]?.offsetWidth ?? 0) +
      gap * (scrollItems.length - 1);
    const containerWidth = containerRef.current?.offsetWidth || 0;

    return totalWidth - containerWidth;
  };

  useSectionNavTheme({
    trigger: "#vmap-how-it-works",
    isDark: false,
    end: () => {
      const scrollDistance = calculateScrollDistance();
      return `+=${scrollDistance + window.innerHeight + 160}`;
    },
  });

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      const section = sectionRef.current;
      const content = contentRef.current;
      const container = containerRef.current;
      const scrollItems = scrollItemsRef.current.filter(Boolean);

      if (!section || !content || !scrollItems.length) return;

      gsap.from(".works-title", {
        opacity: 0,
        y: 40,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      const scrollDistance = calculateScrollDistance();

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: () => `+=${scrollDistance}`,
          scrub: 1,
          pin: container,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const progress = self.progress;

            scrollItems.forEach((item, index) => {
              const progressBar = item?.querySelector(".progress-bar");
              if (progressBar) {
                const itemProgress = Math.max(
                  0,
                  Math.min(1, progress * scrollItems.length - index)
                );
                gsap.set(progressBar, {
                  clipPath: `polygon(0% 0%, ${itemProgress * 100}% 0%, ${
                    itemProgress * 100
                  }% 100%, 0% 100%)`,
                });
              }
            });
          },
        },
      });

      tl.to(scrollItems, {
        x: -scrollDistance,
        duration: 1,
        ease: "none",
      });

      gsap.fromTo(
        scrollItems,
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.2,
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            end: "top 20%",
            scrub: false,
          },
        }
      );
    });
  });

  const workflowSteps = [
    {
      title: "Analysis-Ready Sentinel-2",
      description:
        "The full Sentinel-2 archive is hosted on Terrak.io and exposed as a single queryable resource — clean, cloud-masked and time-aligned — so downstream steps see a consistent continental raster rather than isolated raw scenes.",
      icon: {
        src: "/lottie/dashboard.lottie",
        fallbackSrc: "/icons/dashboard.webp",
        className: "lg:w-[260px] lg:-my-16",
      },
    },
    {
      title: "LiDAR-Derived Training Labels",
      description:
        "Airborne LiDAR surveys give centimetre-scale vertical structure for slices of the continent. Those surveys are turned into high-quality labels of what forest actually is — a better training signal than photo-interpreted polygons.",
      icon: {
        src: "/lottie/air-clock.lottie",
        fallbackSrc: "/icons/multi-temporal.webp",
        speed: 0.7,
      },
    },
    {
      title: "AI Model Training on Terrak.io",
      description:
        "AI models are trained and optimised directly against Terrak.io's training-sample pipeline, which materialises pixel values at labelled points at continental scale — so the model sees the same data representation it will run on in production.",
      icon: {
        src: "/lottie/ai-chip.lottie",
        fallbackSrc: "/icons/ai.webp",
        className: "lg:w-[250px] lg:-mb-20",
        speed: 0.8,
      },
    },
    {
      title: "Annual National Inference",
      description:
        "Once a year the trained model is run over the whole of Australia through Terrak.io's batch orchestrator, producing a fresh forest extent and loss layer for the full continent, versioned against previous years.",
      icon: {
        src: "/lottie/australia-location-pin.lottie",
        fallbackSrc: "/icons/australia-location-pin.webp",
        className: "lg:w-[280px] lg:-my-18",
        speed: 0.3,
      },
    },
    {
      title: "Standards-Compliant Delivery",
      description:
        "The output layers are served back through the same engine as OGC WMS, XYZ web-map tiles, GeoTIFF or NetCDF over any polygon, and CSV zonal statistics — so VMAP drops straight into GIS, web-mapping libraries and compliance workflows.",
      icon: {
        src: "/lottie/plane-send.lottie",
        fallbackSrc: "/icons/send.webp",
        speed: 0.5,
      },
    },
  ];

  return (
    <section
      ref={sectionRef}
      id="vmap-how-it-works"
      className="relative py-20 lg:py-24 bg-haizea-dark text-white overflow-hidden"
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-haizea-green/60 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute top-40 -right-32 w-[500px] h-[500px] bg-haizea-blue/60 rounded-full blur-3xl"
          style={{ animationDelay: "2s" }}
        />
        <div
          className="absolute top-1/2 -left-40 w-[600px] h-[600px] bg-haizea-green/60 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "4s" }}
        />
        <div className="absolute -bottom-40 right-20 w-[550px] h-[550px] bg-haizea-orange/60 rounded-full blur-3xl" />
        <div
          className="absolute top-1/4 right-1/4 w-64 h-64 bg-haizea-blue/50 rounded-full blur-2xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-haizea-green/60 rounded-full blur-3xl"
          style={{ animationDelay: "3s" }}
        />
      </div>
      <Image
        src="/images/vmap-legend.webp"
        alt="Background Satellite Image"
        height={150}
        width={230}
        className="absolute bottom-[50vw] lg:-left-[2%] xl:left-32 z-5 hidden xl:block drop-shadow-2xl/80 w-24 xl:w-32 h-auto"
        draggable={false}
      />
      <Image
        src="/images/vmap-layer.webp"
        alt="Background Satellite Image"
        height={150}
        width={90}
        className="absolute bottom-[50vw] lg:-left-[10%] xl:left-0 z-10 hidden md:block drop-shadow-2xl/80 md:w-76 xl:w-96 h-auto"
        data-speed="0.6"
        draggable={false}
      />
      <Image
        src="/images/bird2.webp"
        alt="Background Bird"
        height={250}
        width={330}
        className="absolute bottom-96 -right-[5%] xl:right-0 opacity-60 z-10 hidden md:block w-64 xl:w-96 h-auto drop-shadow-2xl/50 scale-x-[-1]"
        draggable={false}
        data-speed="0.3"
      />

      <div ref={containerRef} className="container relative">
        <div className="text-center mb-16">
          <Heading2 className="works-title lg:mb-4">How It Works</Heading2>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto">
            Analysis-ready Sentinel-2, airborne LiDAR labels and AI models —
            trained, run and delivered on the same engine, once a year, for the
            whole continent
          </p>
        </div>
        <div
          ref={contentRef}
          className="flex flex-wrap md:flex-nowrap gap-8 overflow-visible"
        >
          {workflowSteps.map((step, index) => (
            <ScrollItem
              key={index}
              ref={(el) => {
                scrollItemsRef.current[index] = el;
              }}
              title={step.title}
              description={step.description}
            >
              <LazyDotLottie
                src={step.icon.src}
                fallbackSrc={step.icon.fallbackSrc}
                className={cn(
                  "mx-auto w-[150px] lg:w-[200px] -my-4 lg:-my-8 aspect-square opacity-70",
                  step.icon.className
                )}
                alt={`${step.title} icon`}
                speed={step.icon.speed}
              />
            </ScrollItem>
          ))}
        </div>
      </div>
      <TheResults />
    </section>
  );
};
